/**
 * DataHub Zod Validation Schemas
 *
 * Runtime validation schemas for DataHub data coming from AppSheet/QBO sync.
 * These schemas ensure that malformed data from external sources doesn't cause
 * silent runtime errors.
 *
 * Usage:
 * ```typescript
 * const result = DataHubGeneratorSchema.safeParse(doc.data());
 * if (!result.success) {
 *   console.warn('Invalid data:', result.error.message);
 * }
 * ```
 */
import { z } from 'zod';
import { SERVICE_OPERATIONS } from '../lib/forms/serviceOperations';
import { CONTRACT_STATUSES } from './contracts/status';

// =============================================================================
// Custom Types
// =============================================================================

/**
 * Custom Zod type for Firestore Timestamp
 * Accepts anything with a toDate() method that returns a Date
 */
const FirestoreTimestamp = z.custom<{ toDate: () => Date }>(
  (val) => val != null && typeof (val as { toDate?: unknown }).toDate === 'function',
  { error: 'Expected Firestore Timestamp' }
);

// =============================================================================
// OfficeHub-owned contract coverage index (Decision 15, SCX #2972)
// =============================================================================

/**
 * One entry of the `officehubContractCoverage` reverse-index stored on a
 * generator record. Runtime shape mirrors ContractCoverageEntry
 * (client/src/types/contracts/coverage.ts). OfficeHub-owned — never from AppSheet.
 */
const CoveredOperationSchema = z.object({
  // Canonical operation — validated against the ServiceOperation taxonomy so a
  // malformed/legacy op string is rejected at the trust boundary, not flowed to C1.
  operation: z.enum([...SERVICE_OPERATIONS] as [string, ...string[]]),
  contractServiceId: z.string(),
  effectiveFrom: FirestoreTimestamp,
  effectiveTo: FirestoreTimestamp.nullable(),
});

export const ContractCoverageEntrySchema = z.object({
  contractId: z.string(),
  contractEquipmentId: z.string(),
  contractStatus: z.enum([...CONTRACT_STATUSES] as [string, ...string[]]),
  contractEndDate: FirestoreTimestamp.nullable(),
  coveredOperations: z.array(CoveredOperationSchema),
  effectiveFrom: FirestoreTimestamp,
  effectiveTo: FirestoreTimestamp.nullable(),
  updatedAt: FirestoreTimestamp,
});

// =============================================================================
// DataHub Generator Schema (appsheet_generators collection)
// =============================================================================

export const DataHubGeneratorSchema = z.object({
  // Required fields (DataHub metadata)
  appId: z.string(),
  syncedAt: FirestoreTimestamp,
  appSheetId: z.string().optional(), // hubDocId reference
  hubDocId: z.string().optional(),

  // AppSheet identifiers (with spaces - original column names)
  'Row ID': z.string().optional(),
  'Generator ID': z.string().optional(),

  // Customer info (AppSheet field names — actual synced names from DataHub)
  'Client Name': z.string().optional().default(''),
  'Client Full Name': z.string().optional(),
  'Site Name': z.string().optional(),
  'Generator Address': z.string().optional(),
  'Generator Address Street': z.string().optional(),
  'Generator Address City': z.string().optional(),
  'Generator Address State': z.string().optional(),
  'Equipment Address Zip': z.string().optional(),
  'Primary Contact': z.string().optional(),

  // Generator specs
  'Generator Make': z.string().optional(),
  'Generator Model': z.string().optional(),
  'Generator Serial Number': z.string().optional(),
  'Generator KW': z.union([z.number(), z.string()]).optional(),
  'Voltage': z.string().optional(),
  'Amps': z.string().optional(),
  'Fuel': z.string().optional(),
  'Generator Type': z.string().optional(),

  // Engine specs
  'Engine Make': z.string().optional(),
  'Engine Model': z.string().optional(),
  'Engine Serial Number': z.string().optional(),

  // Transfer switch (ATS) specs
  'ATS Make': z.string().optional(),
  'ATS Model': z.string().optional(),
  'ATS Serial Number': z.string().optional(),
  'ATS 2 Make': z.string().optional(),
  'ATS 2 Model': z.string().optional(),
  'ATS 2 Serial Number': z.string().optional(),

  // Status and warranty
  'Status': z.string().optional(),
  'Warranty Expires': z.union([z.string(), FirestoreTimestamp]).optional(),
  'Branch': z.string().optional(),

  // Additional info
  'Install Date': z.union([z.string(), FirestoreTimestamp]).optional(),
  'Last Completed Service Date': z.union([FirestoreTimestamp, z.string()]).optional(),
  'Generator Notes': z.string().optional(),
  'Client Notes': z.string().optional(),
  'Inactive Notes': z.string().optional(),

  // OfficeHub-owned contract coverage reverse-index (Decision 15, SCX #2972).
  // Maintained by G1 from `contractEquipment`; C1 recognition reads this to
  // resolve covered contracts for a unit as-of a service date. NEVER created in
  // or written back to AppSheet — the sync must preserve it (C0 churn guard /
  // OFFICEHUB_OWNED_CONTRACT_FIELDS). See client/src/types/contracts/coverage.ts.
  officehubContractCoverage: z.array(ContractCoverageEntrySchema).optional(),
}).passthrough(); // Allow unknown fields from AppSheet

export type DataHubGeneratorValidated = z.infer<typeof DataHubGeneratorSchema>;

// =============================================================================
// DataHub Part Schema (appsheet_parts_list collection)
// =============================================================================

export const DataHubPartSchema = z.object({
  // Required fields (DataHub metadata)
  appId: z.string(),
  syncedAt: FirestoreTimestamp,

  // AppSheet identifiers (original column names)
  'Part ID': z.string().optional(),
  'Row ID': z.string().optional(),

  // Part info
  'Part Number': z.string().optional(),
  'Part Description': z.string().optional(),

  // Pricing (AppSheet stores these as strings)
  'OPG Cost': z.union([z.number(), z.string()]).optional(),
  'QB List Price': z.union([z.number(), z.string()]).optional(),
  'Recommended Price': z.union([z.number(), z.string()]).optional(),

  // Inventory
  'Quantity On Hand': z.union([z.number(), z.string()]).optional(),
  'Bin Location': z.string().optional(),

  // Compatibility
  'Compatible Generator Models': z.string().optional(),
  'Compatible Engine Models': z.string().optional(),
  'Substitutes': z.string().optional(),

  // Classification
  'Category': z.string().optional(),
  'Part Source': z.string().optional(),
  'Branch': z.string().optional(),
  'Item Type': z.string().optional(),
  'Sourcing Difficulty': z.string().optional(),
  'Not A Part': z.string().optional(),

  // Related records
  'Related Job Template Items': z.string().optional(),
  'Related Service Parts': z.string().optional(),
  'Search String': z.string().optional(),

  // Metadata
  'Created': z.string().optional(),
  'Created By': z.string().optional(),
  'Modified': z.string().optional(),
  'Modified By': z.string().optional(),
}).passthrough();

export type DataHubPartValidated = z.infer<typeof DataHubPartSchema>;

// =============================================================================
// Local Price Book Override Schema (priceBookOverrides collection)
// =============================================================================

export const LocalPriceBookOverrideSchema = z.object({
  partId: z.string(),
  // The part's organization, stamped by the server on every write (#6274 Q2 delta review); absent on older overrides.
  organizationId: z.string().optional(),

  // Local price overrides
  customUnitPrice: z.number().optional(),
  customMarkupPercent: z.number().optional(),

  // Additional metadata
  localNotes: z.string().optional(),
  preferredVendor: z.string().optional(),
  internalSKU: z.string().optional(),

  // Category mapping
  lineItemCategory: z.string().optional(),

  // Usage tracking
  usageCount: z.number().optional(),
  lastUsedAt: FirestoreTimestamp.optional(),
  lastUsedInQuote: z.string().optional(),

  // Metadata
  createdAt: FirestoreTimestamp,
  updatedAt: FirestoreTimestamp,
  createdBy: z.string().optional(),
}).passthrough();

export type LocalPriceBookOverrideValidated = z.infer<typeof LocalPriceBookOverrideSchema>;

// =============================================================================
// QBO Customer Schema (qbo_customers collection)
// =============================================================================

export const QBOCustomerSchema = z.object({
  // Required fields
  Id: z.string(),
  DisplayName: z.string(),
  realmId: z.string(),
  qboId: z.string(),
  syncedAt: FirestoreTimestamp,

  // Optional fields - Active may not always be present in QBO response
  Active: z.boolean().optional().default(true),
  CompanyName: z.string().optional(),
  GivenName: z.string().optional(),
  FamilyName: z.string().optional(),
  PrimaryEmailAddr: z.object({
    Address: z.string(),
  }).optional(),
  PrimaryPhone: z.object({
    FreeFormNumber: z.string(),
  }).optional(),
  BillAddr: z.object({
    Line1: z.string().optional(),
    Line2: z.string().optional(),
    City: z.string().optional(),
    CountrySubDivisionCode: z.string().optional(),
    PostalCode: z.string().optional(),
  }).optional(),
  Balance: z.number().optional(),
}).passthrough();

export type QBOCustomerValidated = z.infer<typeof QBOCustomerSchema>;

// =============================================================================
// Local Customer Schema (local_customers collection)
// =============================================================================

export const LocalCustomerSchema = z.object({
  customerId: z.string(),
  companyName: z.string(),
  contactName: z.string(),
  email: z.string(),
  phone: z.string(),
  billingAddress: z.object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  }),
  serviceAddresses: z.array(z.object({
    addressId: z.string(),
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    siteName: z.string().optional(),
  })).optional(),
  paymentTerms: z.string(),
  notes: z.string().optional(),
  source: z.literal('local'),
  createdAt: FirestoreTimestamp,
  updatedAt: FirestoreTimestamp,
}).passthrough();

export type LocalCustomerValidated = z.infer<typeof LocalCustomerSchema>;

// =============================================================================
// DataHub Service Log Schema (appsheet_service_logs collection)
// Uses AppSheet original field names with spaces
// =============================================================================

export const DataHubServiceLogSchema = z.object({
  // DataHub metadata (optional — some legacy docs were synced without appId)
  appId: z.string().optional(),
  // Optional: a fast-save STAND-IN (a ticket still waiting for its number) has not been through the sync, so it has no syncedAt yet.
  syncedAt: FirestoreTimestamp.optional(),
  appSheetId: z.string().optional(),
  hubDocId: z.string().optional(),

  // AppSheet identifiers (original column names)
  'Row ID': z.string().optional(),
  'Service ID': z.string().optional(),
  'Generator ID': z.string().optional(),

  // Customer info
  'Client Name': z.string().optional(),

  // Status
  'Status': z.string().optional(),
  'Stage': z.union([z.number(), z.string()]).optional(),

  // Description
  'Short Description': z.string().optional(),
  'Technician Recommendations': z.string().optional(),
  'Status Notes': z.string().optional(),

  // Assignment
  'Technician': z.string().optional(),
  'Assigned To': z.string().optional(),

  // Scheduling — AppSheet may store these as strings or Firestore Timestamps
  'Scheduled Start Date': z.union([z.string(), FirestoreTimestamp]).optional(),
  'Scheduled Finish Date': z.union([z.string(), FirestoreTimestamp]).optional(),

  // Time tracking
  'Billable Hours': z.union([z.number(), z.string()]).optional(),
  'Generator Run Hours': z.union([z.number(), z.string()]).optional(),

  // Classification
  'Service Type': z.string().optional(),
  'Priority': z.string().optional(),
  'Branch': z.string().optional(),
  'Other Type': z.union([z.array(z.string()), z.string()]).optional(),

  // Billing
  'Invoice Number': z.string().optional(),

  // Metadata — Last Modified may be stored as Timestamp on older docs
  'Created': z.string().optional(),
  'Last Modified': z.union([z.string(), FirestoreTimestamp]).optional(),
  'Archive': z.union([z.boolean(), z.string()]).optional(),
}).passthrough();

export type DataHubServiceLogValidated = z.infer<typeof DataHubServiceLogSchema>;

// =============================================================================
// DataHub Shared Contact Schema (appsheet_shared_contacts collection)
// =============================================================================

export const DataHubSharedContactSchema = z.object({
  // Required fields
  appId: z.string(),
  syncedAt: FirestoreTimestamp,
  hubDocId: z.string(),
  appSheetId: z.string(),

  // AppSheet identifiers
  'Row ID': z.string(),
  'Contact ID': z.string(),

  // Contact info
  'First Name': z.string().optional().default(''),
  'Last Name': z.string().optional().default(''),
  'Salutation': z.string().optional().default(''),
  '_ComputedName': z.string().optional().default(''),
  'Company': z.string().optional().default(''),

  // Contact details
  'Primary Phone': z.string().optional().default(''),
  'Other Phones': z.string().optional().default(''),
  'Primary Email': z.string().optional().default(''),
  'Other Emails': z.string().optional().default(''),

  // Classification
  'Role': z.string().optional().default(''),
  'Commercial Contact': z.string().optional().default('N'),
  'Branch': z.string().optional().default(''),

  // Related records (comma-space separated AppSheet IDs)
  'Generators': z.string().optional().default(''),
  'Related Generators': z.string().optional().default(''),
  'Related Service Logs': z.string().optional().default(''),

  // Display
  'Contact Label': z.string().optional().default(''),
  'Notes': z.string().optional().default(''),

  // Metadata
  'Created': z.string().optional(),
  'Created By': z.string().optional(),
  'Last Modified': z.string().optional(),
  '_RowNumber': z.string().optional(),
  connectionId: z.string().nullable().optional(),
}).passthrough();

export type DataHubSharedContactValidated = z.infer<typeof DataHubSharedContactSchema>;

// =============================================================================
// Validation Helpers
// =============================================================================

/**
 * Helper to validate DataHub data with logging
 * Returns the validated data if successful, or the raw data with a warning if not
 */
export function validateDataHubData<T>(
  schema: z.ZodType<T>,
  data: unknown,
  docId: string,
  entityType: string
): { data: T; isValid: boolean } {
  const result = schema.safeParse(data);

  if (!result.success) {
    console.warn(
      `[DataHub Validation] Invalid ${entityType} data for doc ${docId}:`,
      result.error.message
    );
    // Return raw data cast as T - allows the app to continue but logs the issue
    return { data: data as T, isValid: false };
  }

  return { data: result.data, isValid: true };
}

/**
 * Helper to validate and filter a collection of DataHub documents
 * Invalid documents are logged and optionally filtered out
 */
export function validateDataHubCollection<T>(
  schema: z.ZodType<T>,
  docs: Array<{ id: string; data: () => unknown }>,
  entityType: string,
  options: { filterInvalid?: boolean } = {}
): Array<{ data: T; docId: string; isValid: boolean }> {
  const results = docs.map((doc) => {
    const rawData = doc.data();
    const validation = validateDataHubData(schema, rawData, doc.id, entityType);
    return {
      data: validation.data,
      docId: doc.id,
      isValid: validation.isValid,
    };
  });

  if (options.filterInvalid) {
    const filtered = results.filter((r) => r.isValid);
    const invalidCount = results.length - filtered.length;
    if (invalidCount > 0) {
      console.warn(
        `[DataHub Validation] Filtered out ${invalidCount} invalid ${entityType} documents`
      );
    }
    return filtered;
  }

  return results;
}
